import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface FieldControlProps {
  id: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}

interface FormFieldProps {
  label: string;
  /** Mensagem de erro (já em pt-BR). */
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  /** Nó pronto ou função que recebe as props de acessibilidade para o controle. */
  children: ReactNode | ((props: FieldControlProps) => ReactNode);
}

/** Label acima do campo, dica opcional e erro abaixo, ligados por aria-describedby. */
export function FormField({ label, error, hint, required, className, children }: FormFieldProps) {
  const uid = useId();
  const id = `f${uid.replace(/:/g, '')}`;
  const describedBy = [hint && !error ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;
  const controlProps: FieldControlProps = { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy };
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </label>
      {typeof children === 'function' ? children(controlProps) : children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
